import { Moment } from '~/types'

export type Transaction = {
  createdAt: string | Moment
  amount: string
  currency: string
  accountAmount: string
  accountCurrency: string
  acquirerAmount: string
  acquirerCurrency: string
  originalAmount: string
  originalCurrency: string
  transactionType: TransactionType
  type: TransactionType
  merchantName: string
  state: TransactionState
  reconciliationStatus: ReconciliationStatus
  card: {
    lastFourDigits: string
    opaqueId: string
  }
  customer: Customer
  rrn: string
  errors: boolean
  requestSent: boolean
  cbsReferenceId: string
  cbsTransactionId: string
  merchantId: string
  merchantCategoryCode: string
  reason: TransactionReason
  responseCode: string
}

export type Card = {
  opaqueId: string
  status: CardStatus
  truncatedCardNumber: string
  customer?: Customer
  createdAt: string | Moment
  updatedAt: string | Moment
  latestCardNumber: {
    opaqueId: string
    expiryDate: string
  }
  card_3dsEnrollment: {
    authenticationMethod: string
    destination: string
  }
  balance: {
    amount: string
    currency: string
  }
}

export type Filters = {
  startDate?: Moment
  endDate?: Moment
  minAmount?: string
  maxAmount?: string
  sort?: SORT_TYPE
  transactionState?: TransactionState
  transactionType?: TransactionType
  reason?: TransactionReason
  searchString?: string
  customerName?: string
  cardOpaqueId?: string
  lastFourDigits?: string
  [key: string]: string | number | boolean | Moment | Page | undefined | null
}

export type TabValues = {
  [TRANSACTION_TAB_KEY.TRANSACTIONS]: {
    isTxnLoading: boolean
    transactions: Transaction[]
    totalCount: number
    page: Page
    filters: Filters
  }
  [TRANSACTION_TAB_KEY.FRAUD]: {
    isTxnLoading: boolean
    transactions: Transaction[]
    totalCount: number
    page: Page
    filters: Filters
  }
  [TRANSACTION_TAB_KEY.UNSETTLED]: {
    isTxnLoading: boolean
    transactions: Transaction[]
    totalCount: number
    page: Page
    filters: Filters
  }
  [TRANSACTION_TAB_KEY.CARDS]: {
    isTxnLoading: boolean
    cards: Card[]
    totalCount: number
    page: Page
    filters: Filters
  }
  [TRANSACTION_TAB_KEY.CUSTOMERS]: {
    isTxnLoading: boolean
    customers: Customer[]
    totalCount: number
    page: Page
    filters: Filters
  }
}

export type Customer = {
  name: string
  opaqueId: string
  createdAt?: string | Moment
  cardsIssued?: number
}

export type IssuingPlan = {
  name?: string
  opaqueId: string
}

export type Page = {
  number: number
  size: number
}

export enum SORT_TYPE {
  CREATED_AT_ASC = '+createdAt',
  CREATED_AT_DESC = '-createdAt',
}

export enum CARDS_ACTIONS {
  SET_FILTERS = 'CARDS_ACTIONS.SET_FILTERS',
  SET_PAGE = 'CARDS_ACTIONS.SET_PAGE',
  SET_TOTAL_COUNT = 'CARDS_ACTIONS.SET_TOTAL_COUNT',
  SWITCH_TRANSACTION_TAB = 'CARDS_ACTIONS.SWITCH_TRANSACTION_TAB',
  SET_IS_TXN_LOADING = 'CARDS_ACTIONS.SET_IS_TXN_LOADING',
  SET_TRANSACTIONS = 'CARDS_ACTIONS.SET_TRANSACTIONS',
  SET_FRAUD_EVENTS = 'CARDS_ACTIONS.SET_FRAUD_EVENTS',
  SET_UNSETTLED_TRANSACTIONS = 'CARDS_ACTIONS.SET_UNSETTLED_TRANSACTIONS',
  SET_CUSTOMERS = 'CARDS_ACTIONS.SET_CUSTOMERS',
  SET_CARDS = 'CARDS_ACTIONS.SET_CARDS',
  SET_ISSUING_PLANS = 'CARDS_ACTIONS.SET_ISSUING_PLANS',
  SET_CURRENT_ISSUING_PLAN_OPAQUE_ID = 'CARDS_ACTIONS.SET_CURRENT_ISSUING_PLAN_OPAQUE_ID',
}

export enum TRANSACTION_TAB_KEY {
  TRANSACTIONS = 'Transactions',
  FRAUD = 'Fraud',
  CARDS = 'Cards',
  CUSTOMERS = 'Customers',
  UNSETTLED = 'Unsettled',
}

export type TransactionType =
  | 'refund'
  | 'deduction'
  | 'addition'
  | 'balance_inquiry'
  | 'completion'
  | 'hold'
  | 'overbooking'
  | 'partial_refund'
  | 'partial_reversal'
  | 'reversal'
  | 'void'
  | 'debit_verify'
export type TransactionState = 'COMPLETED' | 'REJECTED'
export type TransactionReason =
  | 'SUCCESS'
  | 'INSUFFICIENT_BALANCE'
  | 'TRANSACTION_NOT_PERMITTED'
  | 'FRAUD_NOT_PERMITTED_TO_TERMINAL'
  | 'CARD_LIMIT_EXCEEDED'
export type CardStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'LOST'
  | 'STOLEN'
  | 'PERM_BLOCK'
  | 'INACTIVE'
  | 'BLOCKED_BY_FRAUD'
export type ReconciliationStatus =
  | 'matched'
  | 'matched_manually'
  | 'pending'
  | 'skipped_due_to_error'
  | 'skipped_not_needed'
  | 'unmatched'

type SetFilters = {
  type: typeof CARDS_ACTIONS.SET_FILTERS
  filters: Filters
}

type SetPage = {
  type: typeof CARDS_ACTIONS.SET_PAGE
  page: Page
}

type SetTotalCount = {
  type: typeof CARDS_ACTIONS.SET_TOTAL_COUNT
  totalCount: number
}

type SwitchTransactionTab = {
  type: typeof CARDS_ACTIONS.SWITCH_TRANSACTION_TAB
  transactionsTabKey: TRANSACTION_TAB_KEY
}

type SetIsTxnLoading = {
  type: typeof CARDS_ACTIONS.SET_IS_TXN_LOADING
  isTxnLoading: boolean
}

type SetTransactions = {
  type: typeof CARDS_ACTIONS.SET_TRANSACTIONS
  transactions: Transaction[]
}

type SetFraudEvents = {
  type: typeof CARDS_ACTIONS.SET_FRAUD_EVENTS
  transactions: Transaction[]
}

type SetUnsettleTransactions = {
  type: typeof CARDS_ACTIONS.SET_UNSETTLED_TRANSACTIONS
  transactions: Transaction[]
}

type SetCustomers = {
  type: typeof CARDS_ACTIONS.SET_CUSTOMERS
  customers: Customer[]
}

type SetCards = {
  type: typeof CARDS_ACTIONS.SET_CARDS
  cards: Card[]
}

type SetIssuingPlans = {
  type: typeof CARDS_ACTIONS.SET_ISSUING_PLANS
  issuingPlans: IssuingPlan[]
}

type SetCurrentIssuingPlanOpaqueId = {
  type: typeof CARDS_ACTIONS.SET_CURRENT_ISSUING_PLAN_OPAQUE_ID
  currentIssuingPlanOpaqueId: string
}

export type ActionTypes =
  | SetFilters
  | SetPage
  | SetTotalCount
  | SwitchTransactionTab
  | SetIsTxnLoading
  | SetTransactions
  | SetFraudEvents
  | SetUnsettleTransactions
  | SetCustomers
  | SetCards
  | SetIssuingPlans
  | SetCurrentIssuingPlanOpaqueId
